import { useRef, useContext } from 'react';
import { Button, CenterScreenModal } from '../atoms'
import { 
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
  useAddExpenseFormModalContext,
} from '@/customHooks'
import { 
  PropertyDetailsContext,
  IncidentDetailContext,
} from '@/contexts'
import {
  CreateExpenseInput,
  ExpenseOriginInput,
  ExpenseOriginOption,
  OrderDirectionOption,
  PageSizeOption,
  useCreateExpenseMutation,
} from '@/graphql/generated';
import { AddExpenseFormControlled } from '../organism';
import { GET_CONTRACT } from '@/graphql/queries/getContract';

const textsData = {
  en: {
    title: 'Add an Expense',
    confirmation: {
      create: {
        title: 'Expense has been created',
        description: (name: string) => `Expense "${name}" has been created successfully`,
      },
    },
    button: {
      confirm: 'Confirm',
      cancel: 'Cancel'
    }
  },
  es: {
    title: 'Crear un Gasto',
    confirmation: {
      create: {
        title: 'Gasto creado con éxito',
        description: (name: string) => `El gasto "${name}" fue creado con éxito`,
      },
    },
    button: {
      confirm: 'Aceptar',
      cancel: 'Volver'
    }
  }
}

export const AddExpenseFormModal = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const expenseDataRef = useRef<CreateExpenseInput | null>(null)
  
  const [createExpense, { loading }] = useCreateExpenseMutation()

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { propertyId, contractId, incidentId, isOpen, closeModal } = useAddExpenseFormModalContext()
  
  const { open: openError } = useErrorHandlerContext()

  // Contextos para refrescar las listas después de crear un gasto (opcionales)
  const propertyContext = useContext(PropertyDetailsContext)
  const incidentContext = useContext(IncidentDetailContext)

  const OnChangeHandle = (expenseData: CreateExpenseInput) => {
    expenseDataRef.current = expenseData
  }

  const onConfirmHandle = () => {
    if (!expenseDataRef.current) return
    
    if (!expenseDataRef.current.date || expenseDataRef.current.date.trim() === '') {
      openError(new Error(userLanguage === 'es' ? 'La fecha es requerida' : 'Date is required'))
      return
    }
    
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(expenseDataRef.current.date)) {
      openError(new Error(userLanguage === 'es' ? 'La fecha debe tener el formato YYYY-MM-DD' : 'Date must be in YYYY-MM-DD format'))
      return
    }
    
    let originInput: ExpenseOriginInput | undefined = undefined
    
    if (incidentId) {
      originInput = {
        expenseOriginOption: ExpenseOriginOption.Incident,
        originEntityId: incidentId,
      }
    } else if (contractId) {
      originInput = {
        expenseOriginOption: ExpenseOriginOption.Contract,
        originEntityId: contractId,
      }
    } else if (propertyId) {
      originInput = {
        expenseOriginOption: ExpenseOriginOption.Property,
        originEntityId: propertyId,
      }
    }
    
    createExpense({ 
      variables: {
        createExpenseInput: {
          title: expenseDataRef.current.title,
          category: expenseDataRef.current.category,
          date: expenseDataRef.current.date,
          invoiceNumber: expenseDataRef.current.invoiceNumber,
          expenseCost: expenseDataRef.current.expenseCost,
          currency: expenseDataRef.current.currency,
          description: expenseDataRef.current.description,
        },
        originInput: originInput,
      },
      refetchQueries: contractId
        ? [{
            query: GET_CONTRACT,
            variables: {
              id: contractId,
              expensesFilterInput: {
                contractId,
                page: '1',
                pageSize: PageSizeOption.OneHundred,
                orderDirection: OrderDirectionOption.Desc,
              },
            },
          }]
        : undefined,
      awaitRefetchQueries: Boolean(contractId),
    })
    .then((response) => {
      closeModal()
      confirmDialogModalOpen({
        title: texts.confirmation.create.title,
        description: texts.confirmation.create.description(response.data?.createExpense.title ?? ''),
      })
      // Refrescar la lista según el contexto - guardar y restaurar para forzar refetch
      if (incidentId && incidentContext && incidentContext.incident) {
        const currentIncident = { ...incidentContext.incident }
        incidentContext.setIncident(undefined)
        queueMicrotask(() => {
          incidentContext.setIncident(currentIncident)
        })
      } else if (propertyId && propertyContext && propertyContext.property) {
        const currentProperty = { ...propertyContext.property }
        propertyContext.setProperty(undefined)
        queueMicrotask(() => {
          propertyContext.setProperty(currentProperty)
        })
      }
    })
    .catch((error) => {
      closeModal()
      openError(error)
    })
  }
  
  return (
    <CenterScreenModal
      isOpen={isOpen}
      onClose={closeModal}
    >
      <h2>{texts.title}</h2>
      <AddExpenseFormControlled onChange={OnChangeHandle} />
      <div className="mt-6 flex justify-center gap-8">
        <Button secondary onClick={closeModal} disabled={loading}>
          {texts.button.cancel}
        </Button>
        <Button onClick={onConfirmHandle} isLoading={loading}>
          {texts.button.confirm}
        </Button>
      </div>
    </CenterScreenModal>
  )
}

